import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {GlobalConstantsComponent} from '../../shared/global-constants/global-constants.component';
import {Observable, of, throwError} from 'rxjs';
import {first} from 'rxjs/operators';
import {PlatformService} from '../platform-service.service';

@Injectable({ providedIn: 'root' })
export class UserService {
  private apiUrl = GlobalConstantsComponent.apiURL;
  private api2Url = GlobalConstantsComponent.apiURL;
  public  userInformations;
  public processCompletion = 0;
  public isMobileUser:boolean;
  isBrowser;
  private localStorage: Storage;


  constructor(
    private http: HttpClient,
    private platformService: PlatformService,
  ) {
    this.localStorage = this.platformService.createLocalStorageServer();
    this.isBrowser = this.platformService.isBrowserPlatform();

    if (this.isBrowser) {
      this.localStorage = localStorage;
    }

  }


  /***********************************************************************************************************************
   *                                                APPELS API
   ***********************************************************************************************************************/

  /**
   * Vérifie qu'un email existe
   * Renvoi : true || false
   * @param email
   */
  checkExist(email){
    return this.http.post(`${this.apiUrl}user/checkExist`, {email: email} );
  }

  /**
   * Vérifie que le mot de passe est conforme aux validateurs et update si c'est ok
   * renvoi error ou succès suivi du message d'erreur/succès
   * @param datas
   */
  updatePassword(datas) {
    return this.http.post(`${this.apiUrl}updatePassword`, datas);
  }

  /**
   * Update les informations du user en base.
   * Update les informations de userInformations dans le service.
   *
   * /!\ Penser au finalize() et y mettre un "this.currentUser = this.userService.getInfos();" pour garder le current user du composant à jour.
   * @param datas
   */
  update(datas) {
     Object.keys(datas).forEach(key => {
       this.setInfo(key, datas[key]);
     })

    const resp = this.http.post(`${this.apiUrl}update`, datas);
    resp.subscribe(d => {
        this.localStorage.removeItem('userSession');
        this.getDatas()
    }
    );
    return resp;
  }

  /**
   * Récupère toutes les informations du user
   */
  getter(){
    return this.http.get(`${this.apiUrl}user`);
  }
  getDatas(force = false): Observable<any> {
    if(!this.getFromLocal('userSession') || force){
      this.http.get(`${this.apiUrl}user`).subscribe(datas => {
        this.storeInLocal('userSession',datas);
      });
      return this.getter();
    }
    const o = of(this.getFromLocal('userSession'));
    return o;
  }

  /**
   * Update des champs précis de userInformations.
   * @param name
   * @param value
   */
  setInfo(name: string, value: any): void {
    if (name === 'homeCity_id' && typeof value === 'number') {
      this.getCityById(value).pipe(first()).subscribe(data => {
        this.userInformations['homeCity_Info'] = data;
      });
    }
    if (name === 'country') {
      this.getCountry(value).pipe(first()).subscribe(data => {
        this.userInformations['homeCountry_Info'] = data;
      });
    }
    if (this.userInformations) this.userInformations[name] = value;

  }

  /**
   * Envoi un email à l'utilisateur
   * Renvoi : success
   * @param email
   */
  forgotPassword(email) {
    return this.http.post(`${this.apiUrl}forgotPassword`, { email });
  }

  /**
   * Reset le mdp du user
   * Renvoi : error + error msg ou success + email du user
   * @param password
   * @param confirmPassword
   * @param token
   */
  resetPassword(password, confirmPassword, token) {
    return this.http.post(`${this.apiUrl}resetPassword`, {
      password: password,
      confirmPassword: confirmPassword,
      token: token,
    });
  }


  /**
   * Envoi un mail au user puis supprime le compte
   * Renvoi : {status:success}
   */
  delete() {
    return this.http.delete(`${this.apiUrl}user/delete`);
  }

  /**
   * Créer un nouvel utilisateur, lui assigne un agent etc...
   * Renvoi:  error + error msg si un problème, sinon : {
   *   status : success,
   *   data : userinfos,
   *   hubspot: null,
   *   user_id: userId
   * }
   * @param user
   */
  register(user) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if(window && window.dataLayer) {
       // eslint-disable-next-line @typescript-eslint/ban-ts-comment
       // @ts-ignore
      window.dataLayer.push({
        'event' : 'sign_up',
        'email':user.email,
      });
    }

    return this.http.post(`${this.apiUrl}register`, user);
  }

  /**
   * update le/les référants du user y compris sur hubspot
   * Renvoi {
   *   status: success,
   *   a: liste des referers
   * }
   * @param campaign
   */
  updateReferer(campaign){
    return this.http.post(`${this.apiUrl}user/setReferer`, {campaign});
  }

  updateUtm(utm){
    return this.http.post(`${this.apiUrl}user/setUtm`, {utm});
  }

  updateClickid(clickid){
    return this.http.post(`${this.apiUrl}user/setClickID`, {clickid});
  }

  /**
   * Supprime des informations précises dans la colonne "informations" du user
   * @param datas - array de keys
   */
  deleteInformationsKey(datas) {
    return this.http.delete(`${this.apiUrl}housing/deleteInformations/` + datas);
  }

  /**
   * Créé ou update la transaction du user sur hubspot
   * Renvoi : {
   *   status : success
   *   message : ok
   *   datas : '' || retour hubspot
   * }
   * @param pipelineId
   * @param dealId
   * @param values
   */
  setHubspotDeal(pipelineId, dealId, values?) {
    let datas = null;
    if(values){
      datas = {datas : values};
    }
    return this.http.post(`${this.api2Url}user/setHubspotDeal/` + pipelineId + '/' + dealId, datas) as any;
  }

  /***********************************************************************************************************************
   *                                                CALCUL/UTILITIES
   ***********************************************************************************************************************/

  /**
   * Retourne l'objet userInformations
   */
  getInfos() {
    return this.userInformations;
  }

  /**
   * Fournit les datas à userInformations
   * @param datas
   */
  setInfos(datas): void {
    this.userInformations = datas;
  }

  /**
   * Stock des datas en local
   * @param name
   * @param value
   */
  storeInLocal(name, value){
    if (this.localStorage) {
      this.localStorage[name] = JSON.stringify(value);
    }
  }

  /**
   * Récupère les datas en local
   * @param name
   */
  getFromLocal(name) {
    if (this.localStorage && this.localStorage[name]) {
      return JSON.parse(this.localStorage[name]);
    }
    return null;
  }

  /**
   * Calcul l'âge de l'utilisateur
   * @param item - date de naissance de l'utilisateur
   */
  calculateAge(item: any) : number {
    const timeDiff = Math.abs(Date.now() - new Date(item).getTime());
    return Math.floor(timeDiff / (1000 * 3600 * 24) / 365.25);
  }

  /**
   * Fonction
   * Set l'adresse sur une ligne
   * @param data
   */
  public formatHomeAddress(data) {
    return (data.homeAddress ? data.homeAddress + ', ' : ''  ) +
      (data.homeCity_Info && data.homeCity_Info.name ? data.homeCity_Info.name : '' )  +
      (data.postalCode ? ' ' + data.postalCode + ', ' : '' )  +
      (data.homeCountry_Info && data.homeCountry_Info.name ?  data.homeCountry_Info.name  : '' );
  }

  getIPAddress()

  {
    /*let ip;
    this.http.get("https://jsonip.com").subscribe( data => {
      console.log(data);
      ip = data
    });
    return ip;*/
    return "0.0.0.0";

  }


  //---------------------------------------------------------------------------------------------------------------------------------------




































  //-------------------------------------------------------------------

  /**
   * Récupère toutes les informations d'une ville grâce à son ID.
   * @TODO : clean quand on aura supprimer la page admin/accom/réservation
   * @param id
   */
  getCityById(id: number) {
    return this.http.get(`${this.apiUrl}getCity/` + id) as any;
  }

  getCityorMetropoleById(id: number) {
    return this.http.get(`${this.apiUrl}getCityorMetropoleById/` + id) as any;
  }

  getCitiesFromMetropole(id: number) {
    return this.http.get(`${this.apiUrl}user/getCitiesFromMetropole/` + id) as any;
  }

  /**
   * Récupère toutes les informations d'un pays grâce à son alpha2Code.
   * @TODO : clean quand on aura supprimer la page admin/accom/réservation
   * @param alphaCode
   */
  getCountry(alphaCode: string) {
    return this.http.get(`${this.apiUrl}getCountry/` + alphaCode) as any;
  }


  /**
   * Renvoi un array avec les villes selon le alpha2Code ; sinon 404
   * @param country
   */
  getCities(country){
    return this.http.get(`${this.apiUrl}getCities/`+country) as any;
  }

  /**
   * Selon l'alpha2Code en param country, on récupère les groupes du pays ; sinon un { status : error, data: error msg }
   * @param country
   */
  getCountryGroups(country){
    return this.http.get(`${this.apiUrl}user/getCountryGroups/`+country) as any;
  }

  //-------------------------------------------------------------------


  /**
   * Inutile ? pas utilisé
   * @param error
   */
  handleError(error: HttpErrorResponse) {
    let errorMessage;
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(errorMessage);
  }



  /**
   * Récupère l'ambassade du user et ses horaires
   * Renvoi : {
   *   status : success,
   *   data: infos
   * }
   */
  getEmbassy(){
    return this.http.get(`${this.apiUrl}user/getEmbassy`)
  }


  //------------------------------------------------------
  /**
   * Créé un nouveau payout en base ; si process = housing certificate, on génère un code promo unique au user
   * Ne renvoi rien
   * @param data
   */
  createPayout(data){
    return this.http.post(`${this.apiUrl}createPayout`, data);
  }

  /**
   * Récupère le dernier payout du user
   * @param service
   */
  getPayout(service, userId = null) {
    let url = `${this.apiUrl}user/getPayout/${service}`;
    if (userId) {
      url += `/${userId}`;
    }
    return this.http.get(url) as any;
  }

    /**
   * Récupère tous les payouts de ce type pour un user
   * @param service
   */
    getAllPayouts(service, userId = null) {
      let url = `${this.apiUrl}user/getAllPayouts/${service}`;
      if (userId) {
        url += `/${userId}`;
      }
      return this.http.get(url) as any;
    }

  /**
   *
   * @param id
   * @param data - {status : 'delete' || 'Canceled' || 'Pending-refund' || 'Not-refunded' || 'Refunded'}
   */
  updatePayout(id: number, data: any) {
    return this.http.post(`${this.apiUrl}updatePayout/`+ id, data) as any;
  }

  /**
   * Si process-key :
   *  - Récupère tous les codes promos du user pour le process associé dont le nom est au moins partiellement égal à abrv
   * Sinon :
   *  - Récupère tous les code promos du user dont le nom est au moins partiellement égal à abrv
   * @param data - { service: '', process-key: '', abrv: '' }
   */
  getPromoCode(data) {
    return this.http.post(`${this.apiUrl}getServicePromoCode`, data);
  }

  /**
   * Si un ID est fournit :
   *  - Vérifie si, parmi tous les booking de l'utilisateur, un d'entre eux à utiliser un code promo lié au user et au service
   * Sinon :
   *  - Vérifie parmi tous les bookings de l'utilisateur si un code promo lié au user à été utilisé
   *
   * Renvoi : {
   *   status : error || success
   *   message : 'Le code promo a déjà été utilisé' || ''
   *   value : true || false
   * }
   * @param id - id du process
   */
  checkUsedPromoCode(id?:number) {
    const url = id ? 'users/checkUsedPromoCode?service='+id : 'users/checkUsedPromoCode';
    return this.http.get(`${this.apiUrl}`+url) as any;
  }

  /**
   * Créé un log pour les paiements paypal
   * @param partnerName
   * @param url
   * @param datas
   * @param response
   */
  createPaymentLog(partnerName,url,datas,response){
    return this.http.post(`${this.apiUrl}partners/createPaymentLog`, {partnerName:partnerName,url:url,datas:datas,response:response} );
  }










  /**
   * @todo : clean ?
   */
  getWebinarsSessions() {
    return this.http.get(`${this.apiUrl}webinar/sessions/upcoming`) as any;
  }

  /**
   * @todo : clean ?
   * @param sessionId
   * @param datas
   */
  registerWebinarSessions(sessionId, datas) {
    return this.http.post(`${this.apiUrl}webinar/sessions/register/` + sessionId, datas) as any;
  }











  /**
   * Récupère un array d'objets selon différents critères
   */
  public getDashboardHeader() {
    return this.http.get(`${this.apiUrl}dashboard/slider/header`) as any;
  }





  /**
   * Récupère une page selon le slug
   * Renvoi : 404 si erreur ; {status: success , page : page} si ok
   * @param slug
   */
  getPageContent(slug, parent) {
    const data = {slug, parent};
    return this.http.post(`${this.apiUrl}getContentPage`, data) as any;
  }

  getAllContentPage(lang, category?, tag?, page?, perPage?) {
    const params = new URLSearchParams({ lang, category, tag, page, perPage }).toString();
    return this.http.get(`${this.apiUrl}getAllContentPage?${params}`) as any;
  }

  getAllContentPageTopic(category, topic) {
    const data = {category, topic};
    return this.http.post(`${this.apiUrl}getAllContentPageTopic`, data) as any;
  }

  getAllTags(lang) {
    return this.http.get(`${this.apiUrl}getAllTags/${lang}`) as any;
  }

  getAllTagsByTopic(name){
    const data = {name};
    return this.http.post(`${this.apiUrl}getAllTagsByTopic`, data) as any;
  }

  getSimilarArticle(tag, slug) {
    const data = {tag, slug};
    return this.http.post(`${this.apiUrl}getSimilarArticle`, data) as any;
  }

  sendCommentary(datas) {
    return this.http.post(`${this.apiUrl}sendCommentary`, datas) as any;
  }

  getCommentariesFromArticle(article_id) {
    return this.http.get(`${this.apiUrl}getCommentariesFromArticle/` + article_id) as any;
  }

  /**
   * Récupère les liens d'une école
   * Renvoi : {
   *   status : success,
   *   datas : array de links
   * }
   */
  getSchoolLinks(){
    return this.http.get(`${this.apiUrl}user/getSchoolLinks`) as any;
  }


  /**
   * Ajoute comme vue toutes les notifications liées à la page actuelle ou au type de notification
   * Si l'utilisateur va sur son profil, ça va update toutes les notifications avec location = my-profile
   * Si l'utilisateur va sur un parcours avec un type, ça va update toutes les notifications en lien avec ce type, peu importe la location
   */
  seenNotifications(menu, type?: string) {
    return this.http.post(`${this.apiUrl}user/seenNotifications`, {menu: menu, type: type});
  }

  getPostReservationsSteps(bookingId){
    return this.http.get(`${this.apiUrl}getPostReservationsSteps/` + bookingId) as any;
  }

  updatePostReservationsSteps(bookingId,stepId){
    return this.http.get(`${this.apiUrl}updatePostReservationsSteps/` + bookingId + '/' + stepId) as any;
  }

}
